import { useCallback, useEffect, useRef, useState } from "react";
import {
  INCIDENT_TYPES,
  INCIDENT_SEVERITIES,
  EXPENSE_CATEGORIES,
  INCIDENT_TYPE_LABELS,
  INCIDENT_SEVERITY_LABELS,
  EXPENSE_CATEGORY_LABELS,
  ENTRY_TASK_KIND_LABELS,
} from "@ckgo2/shared/types";
import { fetchSnapshot, postAction, ApiError, getToken, API_BASE } from "./api";
import { kv, outbox, docs, newClientId, type OutboxItem, type OutboxKind } from "./idb";
import type { Snapshot, GuideEntryTask, Enqueue } from "./types";
import { Card } from "./components/Card";
import { PhotosTab } from "./tabs/PhotosTab";

/** Supplier confirmation PDF URL on the guide-token document endpoint (Block 4.5). */
const docUrl = (id: string) => `${API_BASE}/api/portal/guide/${getToken()}/document/${id}`;
/** Filed arrival-card QR / e-visa confirmation URL on the guide-token endpoint (Block 5.4). */
const entryArtifactUrl = (id: string) =>
  `${API_BASE}/api/portal/guide/${getToken()}/entry-artifact/${id}`;

/** Pull a blob URL into the IndexedDB cache (best-effort) so it opens offline later. */
async function cacheBlob(cacheKey: string, url: string): Promise<void> {
  try {
    if (await docs.get(cacheKey)) return;
    const res = await fetch(url);
    if (res.ok) await docs.set(cacheKey, await res.blob());
  } catch {
    /* offline / best-effort — the viewer falls back to the network when online */
  }
}

/** Open a cached blob (PDF / QR image) — offline cache first, falling back to the network. */
async function viewCachedBlob(cacheKey: string, url: string): Promise<void> {
  let blob = await docs.get(cacheKey).catch(() => undefined);
  if (!blob && navigator.onLine) {
    try {
      const res = await fetch(url);
      if (res.ok) {
        blob = await res.blob();
        await docs.set(cacheKey, blob);
      }
    } catch {
      /* still offline */
    }
  }
  if (!blob) return;
  const objectUrl = URL.createObjectURL(blob);
  window.open(objectUrl, "_blank");
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/** Pre-cache confirmation PDFs + filed entry QRs while online (offline at the airport/border). */
async function cacheOfflineDocs(s: Snapshot): Promise<void> {
  for (const id of s.services.flatMap((sv) => sv.confirmations.map((c) => c.id))) {
    await cacheBlob(id, docUrl(id));
  }
  for (const e of s.entry.filter((e) => e.artifactAvailable)) {
    await cacheBlob(e.id, entryArtifactUrl(e.id));
  }
}

const viewDoc = (id: string) => viewCachedBlob(id, docUrl(id));
const viewEntryArtifact = (id: string) => viewCachedBlob(id, entryArtifactUrl(id));

const SNAP_KEY = () => `snapshot:${getToken()}`;

type Tab = "manifest" | "itinerary" | "incidents" | "expenses" | "photos" | "message";

const fmtDate = (s: string | null) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "—";
const money = (n: number, c: string) => `${new Intl.NumberFormat("en-US").format(n || 0)} ${c}`;

const PATH: Record<OutboxKind, string> = {
  checkin: "/checkin",
  incident: "/incident",
  expense: "/expense",
  photo: "/photo",
};

export default function App() {
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [pending, setPending] = useState<OutboxItem[]>([]);
  const [tab, setTab] = useState<Tab>("manifest");
  const flushing = useRef(false);

  const persist = useCallback(async (s: Snapshot) => {
    setSnap(s);
    await kv.set(SNAP_KEY(), s);
  }, []);

  const flush = useCallback(async () => {
    if (flushing.current || !navigator.onLine) return;
    flushing.current = true;
    try {
      const items = (await outbox.all()).sort((a, b) => a.createdAt - b.createdAt);
      let latest: Snapshot | null = null;
      for (const item of items) {
        try {
          latest = await postAction<Snapshot>(PATH[item.kind], item.payload);
          await outbox.remove(item.clientId);
        } catch (e) {
          // A 4xx will never succeed → drop it; anything else (offline/5xx) → stop and retry later.
          if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
            await outbox.remove(item.clientId);
            continue;
          }
          break;
        }
      }
      if (latest) await persist(latest);
      setPending(await outbox.all());
    } finally {
      flushing.current = false;
    }
  }, [persist]);

  // Initial load: IDB cache first (instant + offline), then a network refresh.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cached = await kv.get<Snapshot>(SNAP_KEY());
        if (alive && cached) {
          setSnap(cached);
          setLoading(false);
        }
      } catch {
        /* no cache */
      }
      try {
        const fresh = await fetchSnapshot<Snapshot>();
        if (!alive) return;
        await persist(fresh);
        setError(null);
        // Best-effort: pull confirmation PDFs + filed entry QRs into IndexedDB now (online) so they
        // open offline later — exactly when a guide needs them (airport with no signal).
        void cacheOfflineDocs(fresh);
      } catch (e) {
        if (alive && !snap) setError(e instanceof Error ? e.message : "Could not load");
      } finally {
        if (alive) setLoading(false);
        setPending(await outbox.all());
        void flush();
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Online/offline → reflect status + flush the outbox when we reconnect.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void flush();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [flush]);

  // Queue a mutation: optimistic local patch + outbox + try to flush.
  const enqueue = useCallback(
    async (
      clientId: string,
      kind: OutboxKind,
      label: string,
      payload: Record<string, unknown>,
      patch: (s: Snapshot) => Snapshot,
    ) => {
      if (snap) await persist(patch(snap));
      await outbox.add({ clientId, kind, payload, label, createdAt: Date.now() });
      setPending(await outbox.all());
      void flush();
    },
    [snap, persist, flush],
  );

  const toggleCheckin = (paxId: string, checkedIn: boolean) =>
    enqueue(
      `checkin:${paxId}`,
      "checkin",
      checkedIn ? "Check-in" : "Undo check-in",
      { participantId: paxId, checkedIn },
      (s) => ({
        ...s,
        pax: s.pax.map((p) =>
          p.id === paxId ? { ...p, checkedInAt: checkedIn ? new Date().toISOString() : null } : p,
        ),
      }),
    );

  if (loading && !snap) return <Splash text="Loading your departure…" />;
  if (!snap) return <Splash text={error || "This link is invalid or has expired."} error />;

  const checkedIn = snap.pax.filter((p) => p.checkedInAt).length;

  return (
    <div className="min-h-screen pb-24 text-gray-900">
      <header className="safe-top bg-brand sticky top-0 z-10 px-4 py-3 text-white shadow">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="truncate text-base font-bold">{snap.departure.tripTitle}</div>
            <div className="text-xs text-white/80">
              {fmtDate(snap.departure.startDate)} – {fmtDate(snap.departure.endDate)}
              {snap.guideName ? ` · ${snap.guideName}` : ""}
            </div>
          </div>
          <SyncBadge online={online} pending={pending.length} />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-3 py-3">
        {tab === "manifest" && (
          <ManifestTab snap={snap} checkedIn={checkedIn} onToggle={toggleCheckin} />
        )}
        {tab === "itinerary" && <ItineraryTab snap={snap} />}
        {tab === "incidents" && <IncidentsTab snap={snap} enqueue={enqueue} />}
        {tab === "expenses" && <ExpensesTab snap={snap} enqueue={enqueue} />}
        {tab === "photos" && <PhotosTab snap={snap} enqueue={enqueue} />}
        {tab === "message" && <MessageTab online={online} snap={snap} onSnap={persist} />}
      </main>

      <TabBar tab={tab} setTab={setTab} pending={pending.length} />
    </div>
  );
}

// ── Shell ────────────────────────────────────────────────────────────────────

function Splash({ text, error }: { text: string; error?: boolean }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6 text-center">
      <div>
        <div className="bg-brand mx-auto mb-4 h-12 w-12 rounded-2xl" />
        <p className={error ? "text-red-600" : "text-gray-600"}>{text}</p>
      </div>
    </div>
  );
}

function SyncBadge({ online, pending }: { online: boolean; pending: number }) {
  const label = !online ? "Offline" : pending > 0 ? `Syncing ${pending}` : "Synced";
  const dot = !online ? "bg-amber-300" : pending > 0 ? "bg-yellow-300" : "bg-emerald-300";
  return (
    <div className="flex shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs">
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      {label}
    </div>
  );
}

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: "manifest", label: "Manifest", icon: "👥" },
  { key: "itinerary", label: "Plan", icon: "🗺️" },
  { key: "incidents", label: "Incidents", icon: "⚠️" },
  { key: "expenses", label: "Expenses", icon: "🧾" },
  { key: "photos", label: "Photos", icon: "📷" },
  { key: "message", label: "Message", icon: "✉️" },
];

function TabBar({ tab, setTab, pending }: { tab: Tab; setTab: (t: Tab) => void; pending: number }) {
  return (
    <nav className="safe-bottom fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white">
      <div className="mx-auto flex max-w-2xl">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium ${
              tab === t.key ? "text-brand" : "text-gray-500"
            }`}
          >
            <span className="text-lg leading-none">{t.icon}</span>
            {t.label}
            {t.key === "expenses" && pending > 0 ? (
              <span className="absolute ml-6 mt-[-2px] h-1.5 w-1.5 rounded-full bg-yellow-400" />
            ) : null}
          </button>
        ))}
      </div>
    </nav>
  );
}

// ── Tabs ───────────────────────────────────────────────────────────────────

function ManifestTab({
  snap,
  checkedIn,
  onToggle,
}: {
  snap: Snapshot;
  checkedIn: number;
  onToggle: (id: string, v: boolean) => void;
}) {
  // Entry formalities grouped per traveller, so each pax row shows its own card status + QR.
  const entryByPax = new Map<string, GuideEntryTask[]>();
  for (const e of snap.entry) {
    const arr = entryByPax.get(e.participantId) ?? [];
    arr.push(e);
    entryByPax.set(e.participantId, arr);
  }
  return (
    <>
      <Card>
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold">
            {checkedIn}/{snap.pax.length} checked in
          </span>
          <span className="text-gray-500">
            {snap.orders.length} {snap.orders.length === 1 ? "booking" : "bookings"}
          </span>
        </div>
      </Card>

      {snap.pax.map((p) => (
        <div key={p.id} className="mb-2 rounded-xl bg-white p-3 shadow-sm">
          <div className="flex items-start gap-3">
            <button
              onClick={() => onToggle(p.id, !p.checkedInAt)}
              className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                p.checkedInAt
                  ? "bg-emerald-500 text-white"
                  : "border-2 border-gray-300 text-transparent"
              }`}
              aria-label="Toggle check-in"
            >
              ✓
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">
                  {[p.firstName, p.lastName].filter(Boolean).join(" ") || "Traveller"}
                </span>
                {p.isLeadPax && (
                  <span className="bg-brand-light text-brand rounded px-1.5 py-0.5 text-[10px] font-semibold">
                    Lead
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-xs text-gray-500">
                {p.orderNumber ? `${p.orderNumber} · ` : ""}
                {p.nationality || "—"}
                {p.passportNumber ? ` · ${p.passportNumber}` : ""}
              </div>
              {p.passportExpiry && (
                <div className="text-xs text-gray-400">
                  Passport exp. {fmtDate(p.passportExpiry)}
                </div>
              )}
              {p.dietary && <div className="mt-1 text-xs text-amber-700">🍽 {p.dietary}</div>}
              <EntryChips tasks={entryByPax.get(p.id) ?? []} />
            </div>
          </div>
        </div>
      ))}

      {snap.orders.some((o) => o.phone) && (
        <Card title="Contacts">
          {snap.orders
            .filter((o) => o.phone)
            .map((o) => (
              <div key={o.orderNumber} className="flex items-center justify-between py-1 text-sm">
                <span>{o.customerName || o.orderNumber}</span>
                <a href={`tel:${o.phone}`} className="text-brand font-medium">
                  {o.phone}
                </a>
              </div>
            ))}
        </Card>
      )}
    </>
  );
}

const ENTRY_STATUS: Record<string, { label: string; cls: string }> = {
  filed: { label: "Filed ✓", cls: "bg-emerald-100 text-emerald-700" },
  open: { label: "Pending", cls: "bg-gray-100 text-gray-600" },
  failed: { label: "Action needed", cls: "bg-amber-100 text-amber-700" },
  skipped: { label: "—", cls: "bg-gray-100 text-gray-500" },
};

// Per-pax entry formality chips on the manifest. A filed card with a stored QR becomes a tappable
// button — the guide opens the traveller's QR at immigration (cached offline). Other statuses are
// just a small status pill so the guide can see at a glance who is/isn't filed yet.
function EntryChips({ tasks }: { tasks: GuideEntryTask[] }) {
  if (tasks.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {tasks.map((t) => {
        const st = ENTRY_STATUS[t.status] ?? ENTRY_STATUS.open;
        const label = `${t.countryCode} ${ENTRY_TASK_KIND_LABELS[t.kind]}`;
        if (t.status === "filed" && t.artifactAvailable) {
          return (
            <button
              key={t.id}
              onClick={() => viewEntryArtifact(t.id)}
              className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"
            >
              🛂 {label} · QR
            </button>
          );
        }
        return (
          <span
            key={t.id}
            className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${st.cls}`}
          >
            {label} · {st.label}
          </span>
        );
      })}
    </div>
  );
}

function ItineraryTab({ snap }: { snap: Snapshot }) {
  return (
    <>
      {snap.services.length > 0 && (
        <Card title="Services">
          {snap.services.map((s) => (
            <div key={s.id} className="border-b border-gray-100 py-2 last:border-0">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium capitalize">
                  {s.kind}: {s.name}
                </span>
                <span className="text-xs capitalize text-gray-500">{s.status}</span>
              </div>
              <div className="text-xs text-gray-400">
                {s.scope === "shared" ? "Shared" : s.orderNumber || "Per order"}
                {s.details && Object.keys(s.details).length
                  ? ` · ${Object.entries(s.details)
                      .slice(0, 3)
                      .map(([k, v]) => `${k}: ${String(v)}`)
                      .join(", ")}`
                  : ""}
              </div>
              {s.customizationNote && (
                <div className="mt-1 text-xs font-medium text-amber-700">
                  ★ {s.customizationNote}
                </div>
              )}
              {s.confirmations.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {s.confirmations.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => viewDoc(c.id)}
                      className="text-brand inline-flex items-center gap-1 rounded bg-violet-50 px-2 py-0.5 text-xs font-medium"
                    >
                      📄 {c.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {snap.orders.some((o) => o.customizations.length > 0) && (
        <Card title="What's different">
          {snap.orders
            .filter((o) => o.customizations.length > 0)
            .map((o) => (
              <div key={o.orderNumber} className="border-b border-gray-100 py-2 last:border-0">
                <div className="text-sm font-medium">
                  {o.customerName || o.orderNumber}
                  <span className="ml-1 text-xs text-gray-400">{o.orderNumber}</span>
                </div>
                <ul className="mt-1 space-y-0.5">
                  {o.customizations.map((c, i) => (
                    <li key={i} className="text-sm text-gray-600">
                      <span className="font-medium">{c.label}</span>
                      {c.detail ? <span className="text-gray-500"> — {c.detail}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </Card>
      )}

      {snap.itinerary.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">No day-by-day itinerary for this trip.</p>
        </Card>
      ) : (
        snap.itinerary.map((d, i) => (
          <Card
            key={i}
            title={d.title ? `Day ${d.day ?? i + 1}: ${d.title}` : `Day ${d.day ?? i + 1}`}
          >
            {d.description && <p className="text-sm text-gray-600">{d.description}</p>}
            {d.highlights && d.highlights.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-sm text-gray-600">
                {d.highlights.map((h, j) => (
                  <li key={j}>{h}</li>
                ))}
              </ul>
            )}
          </Card>
        ))
      )}

      {(snap.departure.meetingPoint ||
        snap.departure.meetingTime ||
        snap.departure.meetingNote) && (
        <Card title="Meeting point">
          {snap.departure.meetingPoint && (
            <div className="font-semibold">{snap.departure.meetingPoint}</div>
          )}
          {snap.departure.meetingTime && (
            <div className="text-sm text-gray-500">Time: {snap.departure.meetingTime}</div>
          )}
          {snap.departure.meetingNote && (
            <div className="mt-1 text-sm text-gray-600">{snap.departure.meetingNote}</div>
          )}
        </Card>
      )}
      {snap.departure.roomingNotes && <Card title="Rooming">{snap.departure.roomingNotes}</Card>}
      {snap.departure.notes && <Card title="Notes">{snap.departure.notes}</Card>}
    </>
  );
}

function IncidentsTab({ snap, enqueue }: { snap: Snapshot; enqueue: Enqueue }) {
  const [type, setType] = useState<string>("delay");
  const [severity, setSeverity] = useState<string>("low");
  const [note, setNote] = useState("");

  const submit = async () => {
    if (!note.trim()) return;
    const clientId = newClientId();
    const created = {
      id: clientId,
      type,
      severity,
      note: note.trim(),
      status: "open",
      reportedByName: snap.guideName,
      createdAt: new Date().toISOString(),
    };
    // Departure-level incident (notifies every garant on the departure); the note carries
    // any traveller specifics. The server ignores extra fields it doesn't expect.
    await enqueue(
      clientId,
      "incident",
      `Incident: ${INCIDENT_TYPE_LABELS[type as keyof typeof INCIDENT_TYPE_LABELS] ?? type}`,
      { type, severity, note: note.trim(), orderId: null, clientId },
      (s) => ({ ...s, incidents: [created, ...s.incidents] }),
    );
    setNote("");
  };

  return (
    <>
      <Card title="Report an incident">
        <div className="grid grid-cols-2 gap-2">
          <Select
            label="Type"
            value={type}
            onChange={setType}
            options={INCIDENT_TYPES}
            labels={INCIDENT_TYPE_LABELS}
          />
          <Select
            label="Severity"
            value={severity}
            onChange={setSeverity}
            options={INCIDENT_SEVERITIES}
            labels={INCIDENT_SEVERITY_LABELS}
          />
        </div>
        <textarea
          className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm"
          rows={3}
          placeholder="What happened?"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          onClick={submit}
          disabled={!note.trim()}
          className="bg-brand mt-2 w-full rounded-lg py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          Log incident
        </button>
      </Card>

      {snap.incidents.map((i) => (
        <div key={i.id} className="mb-2 rounded-xl bg-white p-3 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">
              {INCIDENT_TYPE_LABELS[i.type as keyof typeof INCIDENT_TYPE_LABELS] ?? i.type}
            </span>
            <SeverityChip severity={i.severity} />
          </div>
          <p className="mt-1 text-sm text-gray-700">{i.note}</p>
          <div className="mt-1 text-xs text-gray-400">
            {i.reportedByName || "Guide"} · {fmtDate(i.createdAt)} · {i.status}
          </div>
        </div>
      ))}
    </>
  );
}

function ExpensesTab({ snap, enqueue }: { snap: Snapshot; enqueue: Enqueue }) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("CZK");
  const [category, setCategory] = useState<string>("transport");
  const [supplierName, setSupplierName] = useState("");
  const [note, setNote] = useState("");
  const [receipt, setReceipt] = useState<string | null>(null);

  const onPhoto = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setReceipt(typeof reader.result === "string" ? reader.result : null);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    const amt = Math.round(Number(amount));
    if (!amt || amt < 1) return;
    const clientId = newClientId();
    const created = {
      id: clientId,
      amount: amt,
      currency,
      category,
      note: note.trim() || null,
      createdAt: new Date().toISOString(),
    };
    await enqueue(
      clientId,
      "expense",
      `Expense ${money(amt, currency)}`,
      {
        amount: amt,
        currency,
        category,
        note: note.trim() || null,
        supplierName: supplierName.trim() || null,
        receipt,
        clientId,
      },
      (s) => ({ ...s, expenses: [created, ...s.expenses] }),
    );
    setAmount("");
    setSupplierName("");
    setNote("");
    setReceipt(null);
  };

  // Sum PER CURRENCY — the guide can log USD/EUR/VND/CZK and the PWA is offline (no FX), so a single
  // summed number labelled with the first expense's currency was meaningless. Show a subtotal per
  // currency instead.
  const totalsByCurrency = snap.expenses.reduce<Record<string, number>>((acc, e) => {
    acc[e.currency] = (acc[e.currency] ?? 0) + e.amount;
    return acc;
  }, {});

  return (
    <>
      <Card title="Add expense">
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Label>Amount</Label>
            <input
              type="number"
              inputMode="decimal"
              className="w-full rounded-lg border border-gray-300 p-2 text-sm"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
            />
          </div>
          <div>
            <Label>Currency</Label>
            <select
              className="w-full rounded-lg border border-gray-300 p-2 text-sm"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {["CZK", "EUR", "USD", "VND"].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-2">
          <Select
            label="Category"
            value={category}
            onChange={setCategory}
            options={EXPENSE_CATEGORIES}
            labels={EXPENSE_CATEGORY_LABELS}
          />
        </div>
        <input
          className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm"
          placeholder="Paid to (optional)"
          value={supplierName}
          onChange={(e) => setSupplierName(e.target.value)}
        />
        <input
          className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm"
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-2.5 text-sm text-gray-600">
          {receipt ? "📎 Receipt attached — tap to replace" : "📷 Attach receipt photo"}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => onPhoto(e.target.files?.[0])}
          />
        </label>
        <button
          onClick={submit}
          disabled={!amount || Number(amount) < 1}
          className="bg-brand mt-2 w-full rounded-lg py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          Save expense
        </button>
      </Card>

      {snap.expenses.length > 0 && (
        <Card>
          <div className="mb-2 flex items-center justify-between gap-2 text-sm font-semibold">
            <span>Logged expenses</span>
            <span className="text-right">
              {Object.entries(totalsByCurrency)
                .map(([cur, amt]) => money(amt, cur))
                .join(" · ")}
            </span>
          </div>
          {snap.expenses.map((e) => (
            <div
              key={e.id}
              className="flex items-center justify-between border-b border-gray-100 py-2 text-sm last:border-0"
            >
              <div>
                <div className="font-medium">
                  {EXPENSE_CATEGORY_LABELS[e.category as keyof typeof EXPENSE_CATEGORY_LABELS] ??
                    e.category}
                </div>
                <div className="text-xs text-gray-400">
                  {e.note || "—"} · {fmtDate(e.createdAt)}
                </div>
              </div>
              <span className="font-semibold">{money(e.amount, e.currency)}</span>
            </div>
          ))}
        </Card>
      )}
    </>
  );
}

function MessageTab({
  online,
  snap,
  onSnap,
}: {
  online: boolean;
  snap: Snapshot;
  onSnap: (s: Snapshot) => Promise<void>;
}) {
  return (
    <>
      <PortalMessageCard online={online} snap={snap} onSnap={onSnap} />
      <BroadcastCard online={online} />
    </>
  );
}

// The guide's personal note shown on each traveller's portal (once they're the confirmed lead).
// Online-only (no offline outbox — it's a single editable field, not a field-ops action).
function PortalMessageCard({
  online,
  snap,
  onSnap,
}: {
  online: boolean;
  snap: Snapshot;
  onSnap: (s: Snapshot) => Promise<void>;
}) {
  const [msg, setMsg] = useState(snap.departure.guideMessage ?? "");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setResult(null);
    try {
      const fresh = await postAction<Snapshot>("/guide-message", { message: msg.trim() || null });
      await onSnap(fresh);
      setResult("Saved ✓");
    } catch (e) {
      setResult(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Your message to travellers">
      <p className="mb-2 text-xs text-gray-500">
        Shown in each traveller's portal once you're the confirmed guide — a warm hello goes a long
        way.
      </p>
      {!online && (
        <p className="mb-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
          You're offline — your message can only be saved when connected.
        </p>
      )}
      <textarea
        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
        rows={4}
        placeholder="Hi everyone, I'm your guide for this trip — can't wait to show you around…"
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
      />
      <button
        onClick={save}
        disabled={!online || saving}
        className="bg-brand mt-2 w-full rounded-lg py-2.5 text-sm font-semibold text-white disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save message"}
      </button>
      {result && <p className="mt-2 text-sm text-emerald-700">{result}</p>}
    </Card>
  );
}

function BroadcastCard({ online }: { online: boolean }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const send = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const r = await postAction<{ recipients: number; sent: number }>("/broadcast", {
        subject: subject.trim(),
        body: body.trim(),
      });
      setResult(`Sent to ${r.sent}/${r.recipients} travellers.`);
      setSubject("");
      setBody("");
    } catch (e) {
      setResult(e instanceof Error ? e.message : "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card title="Message all travellers">
      {!online && (
        <p className="mb-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
          You're offline — messages can only be sent when connected.
        </p>
      )}
      <input
        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
        placeholder="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
      />
      <textarea
        className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm"
        rows={5}
        placeholder="Your message to every confirmed traveller on this departure…"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      <button
        onClick={send}
        disabled={!online || sending || !subject.trim() || !body.trim()}
        className="bg-brand mt-2 w-full rounded-lg py-2.5 text-sm font-semibold text-white disabled:opacity-50"
      >
        {sending ? "Sending…" : "Send to all"}
      </button>
      {result && <p className="mt-2 text-sm text-emerald-700">{result}</p>}
    </Card>
  );
}

// ── Small inputs ─────────────────────────────────────────────────────────────

function Label({ children }: { children: React.ReactNode }) {
  return <div className="mb-1 text-xs font-semibold text-gray-500">{children}</div>;
}

function Select({
  label,
  value,
  onChange,
  options,
  labels,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  labels: Record<string, string>;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <select
        className="w-full rounded-lg border border-gray-300 p-2 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {labels[o] ?? o}
          </option>
        ))}
      </select>
    </div>
  );
}

function SeverityChip({ severity }: { severity: string }) {
  const cls =
    severity === "high"
      ? "bg-red-100 text-red-700"
      : severity === "medium"
        ? "bg-amber-100 text-amber-700"
        : "bg-gray-100 text-gray-600";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>
      {INCIDENT_SEVERITY_LABELS[severity as keyof typeof INCIDENT_SEVERITY_LABELS] ?? severity}
    </span>
  );
}
